import { Pipe, PipeTransform, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

const MS_PER_MINUTE = 1000 * 60;
const MS_PER_HOUR = MS_PER_MINUTE * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;
const MS_PER_MONTH = MS_PER_DAY * 30.44;
const MS_PER_YEAR = MS_PER_DAY * 365.25;
const DEFAULT_FALLBACK = 'N/A';
const DEFAULT_PREFIX = 'asset.relativeTime';

@Pipe({ name: 'relativeTime', standalone: true, pure: true })
export class RelativeTimePipe implements PipeTransform {
  readonly transloco = inject(TranslocoService);

  transform(value: Date | string | number | null | undefined, fallback = DEFAULT_FALLBACK, prefix = DEFAULT_PREFIX): string {
    if (!value) return fallback;

    const date = new Date(value);
    if (isNaN(date.getTime())) return fallback;

    const now = new Date();
    const diff = now.getTime() - date.getTime();
    if (diff < 0) return fallback;

    if (this.isSameDay(date, now)) {
      const minutes = Math.floor(diff / MS_PER_MINUTE);
      if (minutes < 1) {
        return this.transloco.translate(`${prefix}.justNow`);
      }
      const hours = Math.floor(diff / MS_PER_HOUR);
      if (hours < 1) {
        return this.transloco.translate(`${prefix}.minutesAgo`, { count: minutes });
      }
      return this.transloco.translate(`${prefix}.hoursAgo`, { count: hours });
    }

    const days = Math.max(1, Math.floor(diff / MS_PER_DAY));
    if (days < 7) {
      return this.transloco.translate(`${prefix}.dayAgo`, { count: days });
    }

    const weeks = Math.floor(days / 7);
    if (weeks < 4) {
      return this.transloco.translate(`${prefix}.weeksAgo`, { count: weeks });
    }

    const months = Math.max(1, Math.floor(diff / MS_PER_MONTH));
    if (months < 12) {
      return this.transloco.translate(`${prefix}.monthsAgo`, { count: months });
    }

    const years = Math.max(1, Math.floor(diff / MS_PER_YEAR));
    return this.transloco.translate(`${prefix}.yearsAgo`, { count: years });
  }

  private isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }
}
